import { useState, FormEvent } from "react";
import { submitForm } from "@/lib/submitForm";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === "string") data.append(key, value);
    });

    // Validate textarea
    const message = formData.get("message") as string;
    if (message && message.length > 500) {
      setError("Message must be 500 characters or fewer.");
      setLoading(false);
      return;
    }

    try {
      const res = await submitForm("general-enquiry", data);
      if (!res.ok) throw new Error(res.error);
      setSubmitted(true);
      form.reset();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Info side */}
          <div>
            <span className="text-teal-600 text-xs font-semibold uppercase tracking-widest">Get In Touch</span>
            <h2 className="text-gray-900 text-4xl md:text-5xl font-extrabold uppercase tracking-tight mt-3 leading-tight">
              Book a<br />
              <span className="text-teal-600">Service</span> Today
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mt-4 max-w-md">
              Ready to start your engineering project? Contact our team and we&apos;ll get back to you within 24 hours. Save up to 20% on selected services.
            </p>

            <div className="mt-8 space-y-5">
              <a
                href="tel:0724114555"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <span className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg group-hover:bg-teal-600 transition-colors">
                  <i className="ri-phone-line text-teal-600 group-hover:text-white transition-colors"></i>
                </span>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest">Phone</p>
                  <p className="text-gray-800 font-semibold text-sm mt-0.5">072 411 4555</p>
                </div>
              </a>

              <a
                href="mailto:info@isman.co.ke"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <span className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg group-hover:bg-teal-600 transition-colors">
                  <i className="ri-mail-line text-teal-600 group-hover:text-white transition-colors"></i>
                </span>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest">Email</p>
                  <p className="text-gray-800 font-semibold text-sm mt-0.5">info@isman.co.ke</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <span className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg">
                  <i className="ri-map-pin-line text-teal-600"></i>
                </span>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-widest">Location</p>
                  <p className="text-gray-800 font-semibold text-sm mt-0.5">Nairobi, Kenya</p>
                </div>
              </div>
            </div>

            {/* Map embed */}
            <div className="mt-10 rounded-lg overflow-hidden h-48">
              <iframe
                title="ISMAN Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255282.35853743808!2d36.6823612!3d-1.3031931!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1172d84d49a7%3A0xf7cf0254b297924c!2sNairobi%2C%20Kenya!5e0!3m2!1sen!2s!4v1690000000000!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form side */}
          <div className="bg-gray-50 rounded-lg p-8 border border-gray-100">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[360px] text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mb-5">
                  <i className="ri-check-double-line text-teal-600 text-2xl"></i>
                </div>
                <h3 className="text-gray-900 font-bold text-xl uppercase tracking-tight">Message Sent!</h3>
                <p className="text-gray-500 text-sm mt-2 max-w-xs">
                  Thanks for reaching out. Our team will get back to you shortly.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-teal-600 text-sm font-semibold hover:underline cursor-pointer"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-gray-900 font-bold text-lg uppercase tracking-wide mb-6">Book / Enquire Now</h3>
                <form
                  data-isman-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold block mb-1.5">Full Name</label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="John Kamau"
                        className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:border-teal-400 transition-colors placeholder:text-gray-300"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold block mb-1.5">Phone</label>
                      <input
                        name="phone"
                        type="tel"
                        placeholder="072 411 XXXX"
                        className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:border-teal-400 transition-colors placeholder:text-gray-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold block mb-1.5">Email</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:border-teal-400 transition-colors placeholder:text-gray-300"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold block mb-1.5">Service Required</label>
                    <select
                      name="service"
                      required
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:border-teal-400 transition-colors cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="3D & 2D Design">3D &amp; 2D Design</option>
                      <option value="Commercial Kitchen">Commercial Kitchen</option>
                      <option value="HVAC Systems">HVAC Systems</option>
                      <option value="Gas & TIG Welding">Gas &amp; TIG Welding</option>
                      <option value="Plumbing">Plumbing</option>
                      <option value="Hospital & Pharmacy Equipment">Hospital &amp; Pharmacy Equipment</option>
                      <option value="Food Processing Equipment">Food Processing Equipment</option>
                      <option value="Stainless Steel Railing">Stainless Steel Railing</option>
                      <option value="General Enquiry">General Enquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold block mb-1.5">
                      Message <span className="normal-case text-gray-400">(max 500 chars)</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your project..."
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:border-teal-400 transition-colors resize-none placeholder:text-gray-300"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-xs">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-teal-600 hover:bg-teal-500 disabled:bg-teal-300 text-white font-bold py-3 rounded-md uppercase text-sm tracking-wider transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {loading ? "Sending..." : "Send Enquiry"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
